import {createTheme, type ThemeOptions} from '@mui/material/styles';
import {CUSTOM_BREAKPOINTS} from "./PortalTheme";

const baseTheme = createTheme({
                                  breakpoints: CUSTOM_BREAKPOINTS,
                              });

const breakpoints = baseTheme.breakpoints;

const testamentTheme: ThemeOptions = {
    breakpoints,
    palette: {
        mode: 'light',
        primary: {
            main: '#1c3959',
            light: '#0076B9',
            contrastText: "#FFFFFF",
        },
        footerLink: {
            main: '#1c3959',
        },
    },
    spacing: 4,
    typography: {
        fontFamily: '"Helvetica Neue LT W01_41488878", Helvetica, Arial, sans-serif;',
        fontSize: 14,
        h1: {
            fontSize: '3rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '-0.09375rem',
            lineHeight: '120%',
            [breakpoints.down('lg')]: {
                fontSize: '2.75rem',
                letterSpacing: '-0.0625rem',
            },
            [breakpoints.down('sm')]: {
                fontSize: '2rem',
                letterSpacing: '-0.0625rem',
            },
        },
        h2: {
            fontSize: '2.75rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '-0.03125rem',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '2.25rem',
            },
            [breakpoints.down('sm')]: {
                fontSize: '1.75rem',
            },
        },
        h3: {
            fontSize: '2rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0px',
            lineHeight: '120%',
            [breakpoints.only('md')]: {
                fontSize: '1.75m',
            },
            [breakpoints.down('sm')]: {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',
            [breakpoints.down('md')]: {
                fontSize: '1.25rem',
            },
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.01563rem',
            lineHeight: '125%',
            [breakpoints.down('md')]: {
                fontSize: '1.188rem',
            },
        },
        h6: {
            fontSize: '1.25rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.01563rem',
            lineHeight: '160%',
            [breakpoints.down('md')]: {
                fontSize: '1.25rem',
            },
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.00938rem',
            lineHeight: '175%'
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: '400',
            letterSpacing: '0.1px',
            lineHeight: '157%'
        },
        body1: {
            fontSize: '1.125rem',
            fontWeight: '400',
            letterSpacing: '0.15px',
            lineHeight: '150%'
        },
        body2: {
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '0.17px',
            lineHeight: '143%'
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: '400',
            letterSpacing: '0.4px',
            lineHeight: '166%'
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: '400',
            letterSpacing: '0.35px',
            lineHeight: '150%'
        },
        accordion: {
            fontSize: '1rem',
            fontWeight: '550',
            lineHeight: '130%',
            letterSpacing: '0.15px',
        },
        linkLabel: {
            fontSize: '1rem',
            fontWeight: '700',
            fontFamily: 'var(--font-family-bold)',
            letterSpacing: '0.15px',
            lineHeight: '150%'
        },
        blockQuote: {
            textAlign: 'center',
            fontSize: '40px',
            fontStyle: 'normal',
            fontWeight: 450,
            lineHeight: '140%',
        },
        landingPageTitle: {
            fontFamily: 'AsapCondensed-ExtraBold',
            fontWeight: 800
        },
        helveticaPageTitle: {
            fontFamily: 'HelveticaNeueLTProBlkCn',
            fontWeight: 900,
            letterSpacing: '0.005em',
            lineHeight: 1.17,
            fontSize: '32px',
            [breakpoints.up('sm')]: {
                fontSize: '40px',
                lineHeight: 1.08
            },
            [breakpoints.up('md')]: {
                fontSize: '48px',
                lineHeight: 1
            },
            [breakpoints.up('lg')]: {
                fontSize: '56px',
                lineHeight: 1.28
            },
            [breakpoints.up('xl')]: {
                fontSize: '80px',
                lineHeight: 1.20
            },
        },
        h3Part: {
            fontSize: '2rem',
            [breakpoints.only('md')]: {
                fontSize: '1.75rem'
            },
            [breakpoints.down('md')]: {
                fontSize: '1.5rem'
            }
        }
    },
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiButton: {
            defaultProps: {
                style: {
                    borderRadius: 999
                }
            }
        },
        MuiCard: {
            defaultProps: {
                style: {
                    borderRadius: 0
                }
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: ({theme}) => ({
                    "&:before": {
                        display: 'none',
                        backgroundColor: "transparent !important",
                    },
                    '& .Mui-expanded .MuiAccordionSummary-content p, .Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                        color: theme.palette.primary.main
                    },

                }),
            },
            defaultProps: {
                style: {
                    borderBottom: "0px solid #333333",
                    borderTop: "1px solid #e0e0e0",
                    borderRadius: "0",
                    boxShadow: "none",
                }
            }
        },
    },
};

declare module '@mui/material/styles' {
    interface TypographyVariants {
        accordion: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        accordion?: React.CSSProperties;
    }
}
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        accordion: true;
    }
}


export default testamentTheme;